import {TagSchema} from 'constants/RegistryConstants';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {ACTION_CREATE_NEW_DRAFT} from 'notebook/store/VersioningConstants';
import {InitialNotePart} from 'notebook/util/NoteUtilConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import EntityStore from 'stores/EntityStore';
import * as net from 'util/Net';

import {
  Template,
  TemplateCollection,
  TemplateCollectionSummary,
  TemplateUpdateObject,
  TemplateUpdateResponse,
} from './TemplateConstants';

export interface TemplatesByVersion {
  [templateVersion: string]: Array<Template>;
}

const TemplateDAO = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  create: ({
    name,
    templateCollectionId,
    isProtected,
    tagSchema,
    schemaTags,
    initialNoteParts,
  }: {
    name: string;
    templateCollectionId: string;
    isProtected: boolean;
    tagSchema?: TagSchema;
    schemaTags?: TagUpdateObject;
    initialNoteParts?: Array<InitialNotePart>;
  }) => {
    const data = {
      name,
      templateCollection: templateCollectionId,
      isProtected,
      tagSchemaId: tagSchema?.id,
      schemaTags,
      initialNoteParts,
    };
    return net.postJSON({url: '/1/api/templates/', data});
  },

  delete: (id: string): JQueryPromise<{}> => net.delete_({url: `/1/api/templates/${id}`}),

  get: (id: string): JQueryPromise<Template> => net.getJSON(`/1/api/templates/${id}`, {view: true}),

  getVersions: (id: string): JQueryPromise<TemplatesByVersion> =>
    net.getJSON(`/1/api/templates/${id}/versions/`),

  update: (id: string, data: TemplateUpdateObject): JQueryPromise<TemplateUpdateResponse> =>
    net.patchJSON({url: `/1/api/templates/${id}`, data}),

  getVersion: (id: string, versionId: string): JQueryPromise<Template> =>
    net.getJSON(`/1/api/templates/${id}/versions/${versionId}`),

  fetchTemplates: (): JQueryPromise<Array<Template>> =>
    net.getJSON('/1/api/templates').then(({data}) => data),

  fetchCollections: (level: string): JQueryPromise<Array<TemplateCollectionSummary>> =>
    net.getJSON('/1/api/template-collections/', {level}).then(({data}) => data),

  fetchCollection: (id: string): JQueryPromise<TemplateCollection> =>
    net.getJSON(`/1/api/template-collections/${id}`),

  templateURL: (templateId: string): string =>
    `/${EntityStore.currentUser().url_identifier}/templates/${templateId}`,

  publishVersion: (
    templateId: string,
    versionId: string,
    versionName: string,
    versionDescription?: string
  ): Promise<{publishingRecordId: string}> =>
    net.postJSONPromise({
      url: `/1/api/templates/${templateId}/versions/${versionId}:publish`,
      data: {versionName, versionDescription},
    }),

  withdrawVersion: (templateId: string, versionId: string): Promise<{publishingRecordId: string}> =>
    net.patchJSONPromise({url: `/1/api/templates/${templateId}/versions/${versionId}:withdraw`}),

  createDraftFromVersion: (
    templateId: string,
    versionId: string
  ): Promise<{
    draftTemplateVersionId: string;
    draftTemplateVersionDbId: number;
  }> => {
    const {metadata} = TemplateStore.getEditorState(templateId);
    return net.postJSONPromise({
      url: `/1/api/templates/${templateId}/versions/${versionId}:create_draft`,
      data: {metadata: {...metadata, action: ACTION_CREATE_NEW_DRAFT}},
    });
  },

  createFromDocument: ({
    documentId,
    name,
    templateCollectionApiId,
    tagSchemaId,
    schemaTags,
  }: {
    documentId: string;
    name: string;
    templateCollectionApiId: string;
    tagSchemaId?: string;
    schemaTags?: TagUpdateObject;
  }): JQueryPromise<{type: string; id: string; url: string}> =>
    net.postJSON({
      url: '/1/api/templates/create-from-document',
      data: {
        documentId,
        name,
        templateCollectionApiId,
        tagSchemaId,
        schemaTags,
      },
    }),
};

export default TemplateDAO;
